/*
 * ap-size-target.js — EIN Ziel fuer jede Groessensuche des Themes.
 *
 * WARUM: Kopfsuche (ap-search-smart.js), Groessenformular (automotive.js,
 * <tyre-size-search>), Kategorie-Vorwahl (ap-prefilter.js) und Felgen-Finder
 * (ap-felgen-finder.js) bauten ihr Ziel bisher jeder fuer sich — meist
 * /search?q=reifen&filter.p.m.tyre.*. Der Suchbestand hinter /search ist aber
 * bei rund 1.000 Dokumenten gedeckelt: fuer 205/55 R16 Sommer kamen dort 146 bis
 * 153 Treffer statt 1.205, fuer Motorrad 120/70 R17 kamen 0 statt 290, und der
 * Felgen-Finder zeigte bei "17 Zoll + LK 5x112" keine einzige 17-Zoll-Felge
 * (Audit 23.09.2026, Befunde finden-groessensuche-zeigt-nur-bruchteil und
 * finden-felgen-finder-zoll-wirkungslos).
 *
 * Das richtige Ziel (Split-Kollektion unter 5.000 mit ?filter.*, sonst die
 * Tag-Route mit dem ECHTEN Groessen-Tag) kennt nur die App: Handles und Tags
 * werden im Theme NIE zusammengesetzt — 130/90-16 existiert als -16, -b16 und
 * -r16 nebeneinander, und LK 5x114.3 heisst im Handle -lk-5x114-3. Eine geratene
 * Adresse rendert still "0 von N" oder 404.
 *
 * VERTRAG mit der App-Proxy-Route (App-Seite: app/routes/proxy.size-target.tsx):
 *
 *   GET /apps/tyre-search/size-target?kind=tyre&scope=<auto|moto|agrar|lkw|
 *       industrie|quad|anhaenger|garten|kart>&w=205&a=55&r=16[&season=Sommer|
 *       Winter|Ganzjahr][&vendor=Michelin]
 *   GET /apps/tyre-search/size-target?kind=wheel&d=17&pcd=5x112[&et=45]
 *
 *   200 {"url": "/collections/...?...", "source": "split"|"tag"|"collection"|
 *        "search", "count": 1205}   oder   200 {"url": null}
 *   oder 200 {"url": null, "retry": true} — vorlaeufig (Verzeichnis der App im Aufbau,
 *        Zeitlimit, Fehler): Rueckfall wie bei null, aber NICHT gemerkt.
 *
 * scope IST KEINE PRODUKTART. 'auto' fasst vier Produktarten zusammen
 * (PKW-, SUV-/Offroad-, Transporter- und Oldtimerreifen — SCOPE_GROUPS in
 * app/lib/catalog/available-sizes.server.ts); das Ziel der Route mischt sie.
 * Eine Seite, deren bisheriges Ziel schon in IHRER Klasse bleibt (Transporter-
 * Produktseite mit data-coll=/collections/transporterreifen, PKW-Kollektion mit
 * Tag-Route), verloere mit diesem Ziel ihre Fahrzeugklasse: PKW-Reifen ohne
 * C-Kennung mit kleinerem Lastindex neben Transporterreifen. Solche Aufrufer
 * fragen deshalb nur mit exactScopeFor(Produktart) — das liefert den scope nur,
 * wenn er GENAU diese eine Produktart meint (moto, agrar, lkw …), sonst null.
 * App-Schritt fuer spaeter: die Route um einen Parameter type=<Produktart>
 * erweitern (Teil des Cache-Schluessels); erst dann duerfen diese Seiten fragen.
 *
 * window.apSizeTarget(params) -> Promise<string|null>. Die Zusage erfuellt sich
 * IMMER (nie reject): mit einer shop-eigenen Adresse oder mit null. Jeder
 * Aufrufer faellt bei null auf sein bisheriges Ziel zurueck — solange die Route
 * nicht live ist, aendert sich fuer Kunden also nichts.
 *
 * Riegel, jeder einzeln noetig:
 *   - ADRESSE: nur ein String, der mit genau EINEM "/" beginnt. "//host" und
 *     "/\host" sind protokoll-relativ (Browser lesen den Rueckstrich als
 *     Schraegstrich), Tabs/Zeilenumbrueche streicht der URL-Parser vor dem
 *     Lesen — beides waere ein Sprung auf eine fremde Seite.
 *   - count 0: ein Ziel, das die App selbst als leer meldet, nie anspringen.
 *   - ZEITLIMIT 2,5 s: der Kunde hat schon auf "Suchen" gedrueckt. Danach gilt
 *     das bisherige Ziel, und die Route wird fuer 5 Minuten nicht mehr gefragt.
 *   - 404 = Route fehlt (noch nicht ausgerollt): fuer den Rest der Sitzung
 *     nicht mehr fragen. {"url": null} ist dagegen eine echte Antwort.
 *   - CACHE je Parameterkombination in sessionStorage (auch die null-Antwort),
 *     dazu ein Speicher laufender Abfragen: fragt die Schnellsuche beim Tippen
 *     vorab, wartet das Absenden nicht noch einmal.
 *
 * Keine Abhaengigkeit, laeuft mehrfach eingebunden genau einmal (mehrere
 * Abschnitte binden die Datei ein, der Browser fuehrt jede <script>-Zeile aus).
 */
(function () {
  'use strict';

  if (typeof window.apSizeTarget === 'function') return;

  var ENDPOINT = '/apps/tyre-search/size-target';
  var TIMEOUT_MS = 2500;
  var PAUSE_AFTER_FAILURE_MS = 5 * 60 * 1000;
  var CACHE_PREFIX = 'ap_size_target:v2:'; // v2 (25.09.2026): die App waehlt Ziele jetzt nach Abdeckung inkl. Notstufe — alte gemerkte null-Antworten der Sitzung duerfen nicht weiter greifen
  var PAUSE_KEY = 'ap_size_target:pause';

  // Reihenfolge der Parameter = Reihenfolge im Vertrag. Sie bildet zugleich den
  // Cache-Schluessel: dieselbe Groesse darf nicht zweimal abgefragt werden, nur
  // weil ein Aufrufer die Felder anders sortiert uebergibt.
  var KINDS = {
    tyre: { keys: ['scope', 'w', 'a', 'r', 'season', 'vendor'], required: ['scope', 'w', 'r'] },
    wheel: { keys: ['d', 'pcd', 'et'], required: ['d', 'pcd'] },
  };

  // Produktart (Shopify product_type, geschrieben von der App in
  // app/lib/catalog/kind.ts) -> scope der Route. Eine Tabelle, damit Formular
  // und Vorwahl dieselbe Zuordnung benutzen. Unbekannte Produktart -> null: dann
  // fragt der Aufrufer gar nicht, statt einen Reifen in die falsche Klasse zu
  // schicken.
  var SCOPES = {
    'PKW-Reifen': 'auto',
    'SUV-/Offroad-Reifen': 'auto',
    'Transporterreifen': 'auto',
    'Oldtimerreifen': 'auto',
    'Motorradreifen': 'moto',
    'Agrarreifen': 'agrar',
    'LKW-Reifen': 'lkw',
    'Industrie- & Staplerreifen': 'industrie',
    'Quad- & ATV-Reifen': 'quad',
    'Anhängerreifen': 'anhaenger',
    'Rasen- & Gartenreifen': 'garten',
    'Kartreifen': 'kart',
  };

  function clean(value) {
    return value === undefined || value === null ? '' : String(value).trim();
  }

  function scopeFor(productType) {
    var key = clean(productType);
    return Object.prototype.hasOwnProperty.call(SCOPES, key) ? SCOPES[key] : null;
  }

  // Der scope nur, wenn er GENAU diese eine Produktart meint — 'auto' (vier
  // Produktarten) liefert null. Fuer Aufrufer, deren bisheriges Ziel die
  // Fahrzeugklasse der Seite haelt (Begruendung im Dateikopf).
  function exactScopeFor(productType) {
    var scope = scopeFor(productType);
    if (!scope) return null;
    var types = 0;
    for (var key in SCOPES) {
      if (Object.prototype.hasOwnProperty.call(SCOPES, key) && SCOPES[key] === scope) types++;
    }
    return types === 1 ? scope : null;
  }

  // Abfrage-Zeichenkette zum Vertrag — oder '' fuer "gar nicht erst fragen".
  function buildQuery(params) {
    if (!params || typeof params !== 'object') return '';
    var kind = clean(params.kind);
    var spec = Object.prototype.hasOwnProperty.call(KINDS, kind) ? KINDS[kind] : null;
    if (!spec) return '';
    for (var i = 0; i < spec.required.length; i++) {
      if (!clean(params[spec.required[i]])) return '';
    }
    var parts = ['kind=' + kind];
    for (var j = 0; j < spec.keys.length; j++) {
      var value = clean(params[spec.keys[j]]);
      if (value) parts.push(spec.keys[j] + '=' + encodeURIComponent(value));
    }
    return parts.join('&');
  }

  // Nur shop-eigene, relative Adressen. Alles andere ist null.
  function safeUrl(url) {
    if (typeof url !== 'string' || url.length < 1) return null;
    if (url.charAt(0) !== '/') return null;
    if (url.charAt(1) === '/') return null;
    // Rueckstrich, Leer- und Steuerzeichen: siehe Kopf. Eine saubere Adresse
    // traegt sie kodiert (%20), nie roh.
    if (/[\\\s\u0000-\u001f\u007f]/.test(url)) return null;
    return url;
  }

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* Speicher aus oder voll — dann eben ohne Cache. */
    }
  }

  // undefined = nichts gemerkt, null = gemerkte Antwort "kein besseres Ziel".
  function readCache(query) {
    var raw = storageGet(CACHE_PREFIX + query);
    if (raw === null) return undefined;
    try {
      var parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && Object.prototype.hasOwnProperty.call(parsed, 'u')) {
        return safeUrl(parsed.u);
      }
    } catch (e) {
      /* fremd beschrieben — neu fragen */
    }
    return undefined;
  }

  function writeCache(query, url) {
    storageSet(CACHE_PREFIX + query, JSON.stringify({ u: url }));
  }

  function paused() {
    var raw = storageGet(PAUSE_KEY);
    if (!raw) return false;
    if (raw === 'off') return true;
    var until = Number(raw);
    return isFinite(until) && until > Date.now();
  }

  function pauseFor(ms) {
    if (storageGet(PAUSE_KEY) === 'off') return;
    storageSet(PAUSE_KEY, String(Date.now() + ms));
  }

  function targetFrom(data) {
    if (!data || typeof data !== 'object') return null;
    if (typeof data.count === 'number' && !(data.count > 0)) return null;
    return safeUrl(data.url);
  }

  function request(query) {
    return new Promise(function (resolve) {
      var settled = false;
      var controller = typeof AbortController === 'function' ? new AbortController() : null;

      function finish(url, remember) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if (remember) writeCache(query, url);
        resolve(url);
      }

      var timer = setTimeout(function () {
        if (settled) return;
        pauseFor(PAUSE_AFTER_FAILURE_MS);
        finish(null, false);
        if (controller) {
          try {
            controller.abort();
          } catch (e) {
            /* schon beendet */
          }
        }
      }, TIMEOUT_MS);

      var options = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
      if (controller) options.signal = controller.signal;

      var pending;
      try {
        pending = window.fetch(ENDPOINT + '?' + query, options);
      } catch (e) {
        finish(null, false);
        return;
      }

      Promise.resolve(pending)
        .then(function (response) {
          if (settled) return null;
          if (!response) {
            finish(null, false);
            return null;
          }
          if (response.status === 404) {
            // Route (noch) nicht ausgerollt: fuer diese Sitzung Ruhe.
            storageSet(PAUSE_KEY, 'off');
            finish(null, false);
            return null;
          }
          if (!response.ok) {
            pauseFor(PAUSE_AFTER_FAILURE_MS);
            finish(null, false);
            return null;
          }
          return response.json().then(function (data) {
            // {"url": null, "retry": true} ist KEINE Antwort auf die Groesse, sondern
            // "gerade nicht" (App-Verzeichnis im Aufbau, Zeitlimit, Fehler). Nicht fuer
            // die Sitzung merken — sonst bliebe diese Groesse bis zum Tab-Schliessen auf
            // dem alten Ziel, obwohl die App eine Minute spaeter antworten koennte.
            var transient = !!(data && typeof data === 'object' && data.retry === true);
            finish(targetFrom(data), !transient);
          });
        })
        .catch(function () {
          if (!settled) pauseFor(PAUSE_AFTER_FAILURE_MS);
          finish(null, false);
        });
    });
  }

  var inflight = {};

  function apSizeTarget(params) {
    var query = buildQuery(params);
    if (!query) return Promise.resolve(null);
    var cached = readCache(query);
    if (cached !== undefined) return Promise.resolve(cached);
    if (paused()) return Promise.resolve(null);
    if (inflight[query]) return inflight[query];
    var pending = request(query).then(function (url) {
      delete inflight[query];
      return url;
    });
    inflight[query] = pending;
    return pending;
  }

  apSizeTarget.scopeFor = scopeFor;
  apSizeTarget.exactScopeFor = exactScopeFor;
  apSizeTarget.query = buildQuery;
  apSizeTarget.safeUrl = safeUrl;
  apSizeTarget.endpoint = ENDPOINT;
  apSizeTarget.timeoutMs = TIMEOUT_MS;

  window.apSizeTarget = apSizeTarget;
})();
